import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MessageCircle, ArrowRight } from "lucide-react";
import api from "../api";
import Seo from "../components/Seo";
import { IMAGES, CONTACT } from "../siteConfig";
import { Reveal } from "../components/Reveal";

const selectCls =
  "w-full bg-obsidian-3 border border-white/10 px-4 py-3 text-sm text-slate-200 focus:border-ember focus:outline-none transition-colors";

const SEGMENT_TABS = [
  { value: "", label: "All" },
  { value: "manglam-enterprises", label: "Mangalam Enterprises" },
  { value: "apex-infra", label: "Apex Infra" },
];

export default function Products() {
  const [searchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    application: searchParams.get("application") || "",
    equipment: searchParams.get("equipment") || "",
    tyre_type: searchParams.get("tyre_type") || "",
    size: searchParams.get("size") || "",
    segment: searchParams.get("segment") || "",
    category: searchParams.get("category") || "",
  });

  useEffect(() => {
    api.get("/products/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
    api
      .get("/products", { params })
      .then((r) => {
        setItems(r.data.items);
        setCount(r.data.count);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [filters]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const selectSegment = (segment) =>
    setFilters({ q: "", application: "", equipment: "", tyre_type: "", size: "", segment, category: "" });

  return (
    <div data-testid="products-page" className="pt-[72px]">
      <Seo
        title="Find the Right Tyre — BKT Tyre Catalogue | Mangalam Group"
        description="Search BKT industrial, OTR and CVR tyres by machine, application, type and size. Serving mining, earthmoving, construction and fleet operators across Jharkhand."
        path="/products"
      />

      <section className="border-b border-white/10 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-ember">Product Discovery</p>
            <h1 className="mt-5 max-w-3xl font-condensed text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[1.0] tracking-tight text-white">
              Find the Right Tyre for Your Application
            </h1>
          </Reveal>

          <Reveal delay={0.1} className="mt-10">
            <div data-testid="product-filters" className="border border-white/10 bg-obsidian-2 p-5 lg:p-6">
              <div className="flex flex-wrap gap-2 mb-5">
                {SEGMENT_TABS.map((t) => (
                  <button
                    key={t.value}
                    data-testid={`segment-tab-${t.value || "all"}`}
                    onClick={() => selectSegment(t.value)}
                    className={`px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                      filters.segment === t.value
                        ? "bg-ember text-[#10141B]"
                        : "border border-white/15 text-slate-300 hover:border-ember hover:text-ember"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <select data-testid="filter-application" value={filters.application} onChange={set("application")} className={selectCls}>
                  <option value="">Application</option>
                  {(meta?.applications || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <select data-testid="filter-equipment" value={filters.equipment} onChange={set("equipment")} className={selectCls}>
                  <option value="">Equipment</option>
                  {(meta?.equipment || []).map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
                <select data-testid="filter-tyre-type" value={filters.tyre_type} onChange={set("tyre_type")} className={selectCls}>
                  <option value="">Tyre Type</option>
                  {(meta?.tyre_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select data-testid="filter-size" value={filters.size} onChange={set("size")} className={selectCls}>
                  <option value="">Tyre Size</option>
                  {(meta?.sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="flex gap-2">
                  <input
                    data-testid="filter-search"
                    placeholder="Search tyres…"
                    value={filters.q}
                    onChange={set("q")}
                    className={selectCls}
                  />
                  <span className="inline-flex items-center bg-ember px-4 text-[#10141B]" aria-hidden="true">
                    <Search className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          </Reveal>

          <p data-testid="product-count" className="mt-8 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">
            {loading ? "Loading…" : `${count} product${count === 1 ? "" : "s"} found`}
          </p>

          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {!loading && items.length === 0 && (
              <div data-testid="products-empty" className="col-span-full border border-white/10 bg-obsidian-2 p-12 text-center">
                <p className="font-condensed text-2xl font-bold uppercase text-white">No tyres match these filters</p>
                <p className="mt-2 text-sm text-slate-400">Try clearing a filter, or send us your requirement directly.</p>
              </div>
            )}
            {items.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.06}>
                <article data-testid={`product-card-${p.id}`} className="group flex h-full flex-col border border-white/10 bg-obsidian-2 transition-colors hover:border-white/25">
                  <Link to={`/products/${p.id}`} data-testid={`product-link-${p.id}`} className="relative block h-44 overflow-hidden">
                    <img
                      src={p.image || IMAGES.tread}
                      alt={`${p.brand} ${p.name} ${p.size} tyre`}
                      className="h-full w-full object-cover img-industrial transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 bg-obsidian/85 backdrop-blur-sm px-2.5 py-1 font-mono text-[9px] tracking-[0.22em] uppercase text-white">
                      {p.brand}
                    </span>
                    {p.sample && (
                      <span className="absolute right-4 top-4 border border-white/20 bg-obsidian/85 backdrop-blur-sm px-2 py-1 font-mono text-[8px] tracking-[0.2em] uppercase text-slate-400">
                        Sample
                      </span>
                    )}
                  </Link>
                  <div className="flex flex-1 flex-col p-6">
                    <Link to={`/products/${p.id}`} className="block">
                      <p className="font-mono text-[11px] tracking-[0.22em] text-ember">{p.size}</p>
                      {(p.sizes || []).length > 1 && (
                        <p className="mt-0.5 font-mono text-[9px] tracking-[0.18em] uppercase text-slate-400">
                          {p.sizes.length} sizes available
                        </p>
                      )}
                      <h3 className="mt-1.5 font-condensed text-2xl font-bold uppercase tracking-wide text-white transition-colors group-hover:text-ember">
                        {p.name}
                      </h3>
                    </Link>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {[p.tyre_type, p.construction, p.position, ...(p.equipment || [])]
                        .filter(Boolean)
                        .map((t) => (
                          <span key={t} className="border border-white/10 px-2 py-0.5 font-mono text-[9px] tracking-[0.15em] uppercase text-slate-400">
                            {t}
                          </span>
                        ))}
                    </div>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400 line-clamp-3">{p.description}</p>
                    <div className="mt-5 flex items-center justify-between gap-3">
                      <a
                        href={`${CONTACT.whatsapp}?text=${encodeURIComponent(`Enquiry: ${p.brand} ${p.name} — ${p.size}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-enquire-${p.id}`}
                        className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-ember hover:text-white transition-colors"
                      >
                        <MessageCircle className="h-3.5 w-3.5" /> Enquire
                      </a>
                      <Link
                        to={`/products/${p.id}`}
                        data-testid={`product-view-${p.id}`}
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-300 hover:text-ember transition-colors"
                      >
                        View Specs <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
